/**
 * StudioChatMessage — one message bubble.
 *
 * User messages are right-aligned with a dark bubble; assistant messages
 * are left-aligned with no bubble (plain text rendering). Tool-call pills
 * sit under the assistant message when present.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Check, X as XIcon } from 'lucide-react'
import type { ChatMessage } from '@/stores/useStudioStore'

export function StudioChatMessage({ msg }: { msg: ChatMessage }) {
  const [showThinking, setShowThinking] = useState(false)

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%] bg-neutral-800 border border-neutral-700 rounded-2xl rounded-br-md px-3 py-2 text-sm text-neutral-100 whitespace-pre-wrap break-words">
          {msg.content}
        </div>
      </div>
    )
  }

  return (
    <div className="mb-4">
      {msg.thinking && (
        <button
          type="button"
          onClick={() => setShowThinking((v) => !v)}
          className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-neutral-300 mb-1"
        >
          {showThinking ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          thinking
        </button>
      )}
      {msg.thinking && showThinking && (
        <div className="mb-2 px-3 py-2 bg-neutral-900/50 border-l-2 border-neutral-700 text-[11px] text-neutral-400 whitespace-pre-wrap leading-relaxed font-light">
          {msg.thinking}
        </div>
      )}
      <div className="text-sm text-neutral-100 whitespace-pre-wrap leading-relaxed font-light break-words">
        {msg.content}
      </div>
      {msg.tool_calls && msg.tool_calls.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {msg.tool_calls.map((tc, i) => (
            <div
              key={i}
              className={
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border font-light ' +
                (tc.ok
                  ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'
                  : 'bg-red-950/40 border-red-800/50 text-red-300')
              }
              title={tc.summary}
            >
              {tc.ok ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
              <span>{tc.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
